/**
 * Pagamento de uma cobranca (spec 003, 4.3; spec 007, M7): a mensalidade ou o portao do avanco. O link sai da area (quem
 * tem o link paga) e a pagina e a mesma maquina do checkout, sem o passo dos dados: quem deve ja e conhecido. A sessao fica
 * no link (`?s=`), entao refresh e aba reaberta voltam pro mesmo PIX. Pagou por fora: o comprovante vai pela area (spec 039).
 * Nao pede login (spec 045, 3.9): so ganhou o Voltar pro site da marca, no botao e no navegador.
 */
import type { EstadoDaTentativa, VitrineDeCobranca } from "@mtc/contratos";
import { Aviso, Botao, BotaoLink, Campo, Carregando, LinkInterno, PagamentoPix, Pilha, ResumoDeCompra, TelaDeAcesso, formatarDia, formatarReais } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router";
import { marca } from "../config";
import { useVoltarProSite } from "../voltar";
import { chamar, Instabilidade, Pagar, type Oferta } from "./pagamento";

/**
 * Pagou por fora: o comprovante vai pela area, na propria mensalidade ou no pedido (spec 039). O WhatsApp saiu daqui
 * (Vitor, 30/09): ficou so pro comprovante de outro pagamento, no "Falar com a equipe".
 */
function PagouPorFora() {
  return (
    <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>
      Já pagou por fora? Envie o comprovante pela sua área, em <LinkInterno href="/mensalidades">Mensalidades</LinkInterno> ou, se for o avanço de etapa, em <LinkInterno href="/solicitacoes">Solicitações</LinkInterno>.
    </p>
  );
}

export function PaginaPagar() {
  const { token = "" } = useParams();
  const [busca, setBusca] = useSearchParams();
  const sessao = busca.get("s") ?? "";
  const qc = useQueryClient();
  const vitrine = useQuery({ queryKey: ["cobranca", token], queryFn: () => chamar<VitrineDeCobranca>(`/v1/cobrancas/${encodeURIComponent(token)}`), retry: false });
  const estado = useQuery({
    queryKey: ["tentativa", sessao],
    queryFn: () => chamar<EstadoDaTentativa>(`/v1/tentativas/${encodeURIComponent(sessao)}`),
    enabled: Boolean(sessao),
    retry: false,
    refetchInterval: (q) => (q.state.data?.ok && q.state.data.dados.pedido?.status === "AGUARDANDO" ? 3_000 : false),
  });
  const m = marca();
  // O link de pagamento aberto direto (e-mail, WhatsApp): voltar leva pro site da marca, como no checkout (spec 045, 3.7).
  useVoltarProSite(m.linkPlanos);
  const [telefone, setTelefone] = useState("");
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const tentou = useRef(false);

  const v = vitrine.data;
  const vit = v?.ok ? v.dados : null;
  const abrir = async (tel?: string) => {
    setAbrindo(true);
    setErro(null);
    const r = await chamar<{ sessao: string }>(`/v1/cobrancas/${encodeURIComponent(token)}/sessao`, { method: "POST", body: JSON.stringify(tel ? { telefone: tel } : {}) });
    setAbrindo(false);
    if (r.ok) setBusca({ s: r.dados.sessao }, { replace: true });
    else if (r.fora) setErro("");
    else setErro(r.erro);
  };
  // Com o celular guardado, a sessao abre sozinha: o trader ja cai na escolha do meio.
  useEffect(() => {
    if (vit && vit.status === "ABERTA" && !vit.pede_telefone && !sessao && !tentou.current) {
      tentou.current = true;
      void abrir();
    }
  }, [vit, sessao]);

  const palco = (conteudo: React.ReactNode) => (
    <TelaDeAcesso marca={m} rotulo="Pagamento seguro" frase={vit ? `${vit.descricao}, ${formatarReais(vit.valor_centavos)}` : undefined} voltarHref={m.linkPlanos}>
      {conteudo}
    </TelaDeAcesso>
  );
  if (vitrine.isLoading) return palco(<Carregando compacto texto="Carregando o pagamento" />);
  if (!v || !v.ok || !vit) {
    return palco(
      <Pilha espaco={4}>
        <h1 className="mtc-titulo mtc-titulo--2">Pagamento</h1>
        {v && !v.ok && v.inexistente ? <Aviso tipo="erro">Esta cobrança não existe. Abra de novo pela área do trader, em Mensalidades.</Aviso> : v && !v.ok && !v.fora ? <Aviso tipo="erro">{v.erro}</Aviso> : <Instabilidade />}
      </Pilha>,
    );
  }
  const t = sessao ? estado.data : undefined;
  const pedido = t?.ok ? t.dados.pedido : null;
  const oferta: Oferta = { meios: vit.meios, parcelas: [{ parcelas: 1, valor_parcela_centavos: vit.valor_centavos, total_centavos: vit.valor_centavos, juros_centavos: 0 }], chave_publica: vit.chave_publica, preco_centavos: vit.valor_centavos };
  const pago = vit.status === "PAGA" || pedido?.status === "PAGO";
  const conteudo = pago ? (
    <Pilha espaco={3}>
      <Aviso tipo="sucesso">Pagamento confirmado. Obrigado, {vit.primeiro_nome}! Já está registrado na sua área.</Aviso>
      <BotaoLink href="/" variante="primario">Ir pra área do trader</BotaoLink>
    </Pilha>
  ) : vit.status === "CANCELADA" ? (
    <Pilha espaco={3}>
      <Aviso tipo="alerta">Esta cobrança foi cancelada. Se você já pagou, fale com o suporte.</Aviso>
      <BotaoLink href="/mensalidades">Ver minhas mensalidades</BotaoLink>
    </Pilha>
  ) : !sessao ? (
    vit.pede_telefone ? (
      <form onSubmit={(ev: FormEvent) => { ev.preventDefault(); void abrir(telefone); }} noValidate>
        <Pilha espaco={4}>
          <Campo rotulo="Celular com DDD" valor={telefone} aoMudar={setTelefone} inputMode="tel" autoComplete="tel" placeholder="(11) 98765-4321" dica="O processador do pagamento pede um celular." required />
          {erro ? <Aviso tipo="erro">{erro}</Aviso> : erro === "" ? <Instabilidade /> : null}
          <Botao type="submit" largo carregando={abrindo} disabled={telefone.replace(/\D/g, "").length < 10}>Continuar pro pagamento</Botao>
        </Pilha>
      </form>
    ) : erro ? (
      <Aviso tipo="erro">{erro}</Aviso>
    ) : erro === "" ? (
      <Instabilidade />
    ) : (
      <Carregando compacto texto="Abrindo o pagamento" />
    )
  ) : estado.isLoading ? (
    <Carregando compacto texto="Conferindo o pagamento" />
  ) : t && !t.ok ? (
    t.status === 404 || t.status === 410 ? (
      <Pilha espaco={3}>
        <Aviso tipo="alerta">{t.erro || "Esta sessão de pagamento expirou."}</Aviso>
        <Botao onClick={() => { tentou.current = false; setBusca({}, { replace: true }); }}>Começar de novo</Botao>
      </Pilha>
    ) : (
      <Instabilidade />
    )
  ) : pedido?.status === "AGUARDANDO" && pedido.pix ? (
    <PagamentoPix qrCodeUrl={pedido.pix.qr_code_url} copiaECola={pedido.pix.qr_code} expiraEm={pedido.pix.expira_em} />
  ) : pedido?.status === "AGUARDANDO" ? (
    <Carregando compacto texto="Processando o pagamento" />
  ) : (
    <Pagar vitrine={oferta} sessao={sessao} aviso={pedido?.status === "RECUSADO" ? { tipo: "erro", texto: pedido.recusa ?? "Pagamento recusado." } : pedido?.status === "EXPIRADO" ? { tipo: "alerta", texto: "O PIX anterior expirou. Gere outro abaixo." } : null} aoPagar={() => void qc.invalidateQueries({ queryKey: ["tentativa", sessao] })} />
  );
  return palco(
    <Pilha espaco={5}>
      <ResumoDeCompra nome={vit.descricao} detalhe={vit.vence_em ? `Vence em ${formatarDia(vit.vence_em)}` : null} precoCentavos={vit.valor_centavos} />
      {conteudo}
      {!pago && vit.status !== "CANCELADA" && <PagouPorFora />}
    </Pilha>,
  );
}
